import { slicedToArray as _slicedToArray, objectSpread2 as _objectSpread2 } from '../../../_virtual/_rollupPluginBabelHelpers.js';
import React__default, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { SEARCH_BAR_ENGAGED_EVENT } from '../../../util/analyticsConstants.js';
import aa from 'search-insights';
import { ALGOLIA_CLIENT, ALGOLIA_APP_ID } from '../../../util/algolia.js';
import { getQueryParam } from '../../../util/queryString.js';

var DEFAULT_TRACKING = {
  updateQuery: function updateQuery() {},
  sendSearchResultSectionEvent: function sendSearchResultSectionEvent() {},
  sendQuerySubmittedEvent: function sendQuerySubmittedEvent() {},
  sendSearchBarEngagedEvent: function sendSearchBarEngagedEvent() {},
  sendSearchResultClickEvent: function sendSearchResultClickEvent() {},
  isTrackingInitialized: function isTrackingInitialized() {
    return false;
  }
};
var QuerySuggestionsSearchInputTrackingContext = /*#__PURE__*/createContext(DEFAULT_TRACKING);
var QuerySuggestionsSearchInputTrackingProvider = function QuerySuggestionsSearchInputTrackingProvider(_ref) {
  var children = _ref.children,
    applicationName = _ref.applicationName,
    initialQuery = _ref.query,
    logger = _ref.logger;
  var _useState = useState(initialQuery),
    _useState2 = _slicedToArray(_useState, 2),
    query = _useState2[0],
    setQuery = _useState2[1];
  var _useState3 = useState(undefined),
    _useState4 = _slicedToArray(_useState3, 2),
    algoliaUserToken = _useState4[0],
    setAlgoliaUserToken = _useState4[1];
  useEffect(function () {
    try {
      aa('init', {
        appId: ALGOLIA_APP_ID,
        apiKey: ALGOLIA_CLIENT,
        useCookie: true //sets an automatic user token (browser session level) so that events will be sent
      });
      aa('getUserToken', null, function (err, token) {
        if (err) {
          logger === null || logger === void 0 || logger.warning('error getting Algolia user token', {
            contextData: {
              source: 'omnisearch',
              error: err.message,
              query: query,
              algoliaApplication: applicationName
            }
          });
          return;
        }
        setAlgoliaUserToken(token);
      });
      aa('onUserTokenChange', function (token) {
        setAlgoliaUserToken(token);
      }, {
        immediate: true
      });
    } catch (_unused) {}
  }, []);
  var updateQuery = function updateQuery(newQuery) {
    setQuery(newQuery);
  };
  var sendSearchResultSectionEvent = function sendSearchResultSectionEvent(searchResultSectionEventInput) {
    var query = searchResultSectionEventInput.query,
      taxonomyId = searchResultSectionEventInput.taxonomyId,
      numberOfResultsToShow = searchResultSectionEventInput.numberOfResultsToShow,
      indexName = searchResultSectionEventInput.indexName,
      results = searchResultSectionEventInput.results,
      rules = searchResultSectionEventInput.rules;
    var resultsWithNbTypos = results.filter(function (result) {
      var _result$_rankingInfo$, _result$_rankingInfo;
      return ((_result$_rankingInfo$ = (_result$_rankingInfo = result._rankingInfo) === null || _result$_rankingInfo === void 0 ? void 0 : _result$_rankingInfo.nbTypos) !== null && _result$_rankingInfo$ !== void 0 ? _result$_rankingInfo$ : 0) > 0;
    });
    var trackingEvent = {
      query: query,
      location: window.location.pathname,
      applicationName: applicationName,
      sectionName: taxonomyId,
      resultsInSection: results.slice(0, 200).map(function (searchItem) {
        return {
          resultSource: indexName,
          id: searchItem.objectID
        };
      }),
      rulesApplied: rules,
      resultsWithTyposCount: resultsWithNbTypos.length,
      totalResultCount: results.length,
      shownResultsInSection: results.slice(0, numberOfResultsToShow).map(function (searchItem) {
        return {
          resultSource: indexName,
          id: searchItem.objectID
        };
      })
    };
    window.tracking.track('Search Result Section', trackingEvent);
  };
  var sendQuerySubmittedEvent = function sendQuerySubmittedEvent(args) {
    var label = args.label,
      queryToOverride = args.queryToOverride,
      searchTerm = args.searchTerm;
    window.tracking.track('Search Submitted', {
      query: queryToOverride || query,
      application: applicationName,
      location: window.location.pathname,
      label: label,
      searchTerm: searchTerm || query
    });
  };
  var sendSearchBarEngagedEvent = function sendSearchBarEngagedEvent(label) {
    window.tracking.track(SEARCH_BAR_ENGAGED_EVENT, {
      application: applicationName,
      location: window.location.pathname,
      label: label
    });
  };
  var sendSearchBarEngagedEventCallback = useCallback(function (label) {
    sendSearchBarEngagedEvent(label);
  }, []);
  var sendSearchResultClickEvent = function sendSearchResultClickEvent(args) {
    var _searchItem$algoliaRe;
    var eventName = args.eventName,
      destinationUrl = args.destinationUrl,
      searchItem = args.searchItem;
    var _searchItem$trackingI = searchItem.trackingInformation.auxiliaryData,
      pageName = _searchItem$trackingI.pageName,
      totalItems = _searchItem$trackingI.totalItems;
    var _searchItem$trackingI2 = searchItem.trackingInformation.eventData,
      resultPosition = _searchItem$trackingI2.resultPosition,
      sectionPosition = _searchItem$trackingI2.sectionPosition,
      position = _searchItem$trackingI2.position;
    window.tracking.track(eventName, _objectSpread2(_objectSpread2({
      category: 'Internal Search'
    }, searchItem.trackingInformation.eventData), {}, {
      resultDestination: destinationUrl,
      query: query,
      label: query,
      searchTerm: query,
      application: applicationName,
      eventDetail: "".concat(pageName || window.location.pathname, ";").concat(destinationUrl, ";").concat(totalItems, ";").concat(resultPosition + 1, ";").concat(sectionPosition + 1, ";").concat(position),
      resultIndex: searchItem === null || searchItem === void 0 ? void 0 : searchItem.algoliaResult.indexName,
      algoliaUserToken: algoliaUserToken,
      algoliaQueryId: searchItem === null || searchItem === void 0 ? void 0 : searchItem.algoliaResult.queryId,
      algoliaIndexPosition: searchItem === null || searchItem === void 0 ? void 0 : searchItem.algoliaResult.searchPosition,
      routingEligible: !!getQueryParam('reroutedSearch'),
      hasTypo: (searchItem === null || searchItem === void 0 || (_searchItem$algoliaRe = searchItem.algoliaResult) === null || _searchItem$algoliaRe === void 0 || (_searchItem$algoliaRe = _searchItem$algoliaRe.rankingInfo) === null || _searchItem$algoliaRe === void 0 ? void 0 : _searchItem$algoliaRe.nbTypos) > 0
    }));
    try {
      aa('clickedObjectIDsAfterSearch', {
        eventName: "Search Result Clicked - ".concat(applicationName),
        index: searchItem === null || searchItem === void 0 ? void 0 : searchItem.algoliaResult.indexName,
        queryID: searchItem === null || searchItem === void 0 ? void 0 : searchItem.algoliaResult.queryId,
        objectIDs: [searchItem.objectID],
        positions: [searchItem === null || searchItem === void 0 ? void 0 : searchItem.algoliaResult.searchPosition]
      });
    } catch (ex) {
      logger === null || logger === void 0 || logger.warning('Failed to send search insights event', {
        contextData: {
          source: 'omnisearch',
          error: ex.message,
          query: query,
          algoliaApplication: applicationName
        }
      });
    }
  };
  var isTrackingInitialized = function isTrackingInitialized() {
    return !!window.tracking;
  };
  var trackingProviderValue = {
    updateQuery: updateQuery,
    sendSearchResultSectionEvent: sendSearchResultSectionEvent,
    sendQuerySubmittedEvent: sendQuerySubmittedEvent,
    sendSearchBarEngagedEvent: sendSearchBarEngagedEventCallback,
    sendSearchResultClickEvent: sendSearchResultClickEvent,
    isTrackingInitialized: isTrackingInitialized
  };
  return /*#__PURE__*/React__default.createElement(QuerySuggestionsSearchInputTrackingContext.Provider, {
    value: trackingProviderValue
  }, children);
};
var useTracking = function useTracking() {
  return useContext(QuerySuggestionsSearchInputTrackingContext);
};

export { QuerySuggestionsSearchInputTrackingProvider, useTracking };
//# sourceMappingURL=QuerySuggestionsSearchInputTrackingProvider.js.map
